import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { coursesClient } from '../apis/client-instances';
import { UserRole, CourseType } from '../apis/api-client';
import { removeCourse, updateCourse } from '../features/courses/coursesSlice';
import { isAdmin } from '../utils/roleHelpers';
import { alert, getFileUrl } from './function';
import { RootState } from '../types';

// Type for path course
interface PathCourseItem {
  courseId: number;
  courseTitle: string;
  courseThumbnail?: string;
  sequenceOrder: number;
}

export function useCourseManagement(courseId: number) {
  const [course, setCourse] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>(undefined);

  const currentUser = useSelector((state: RootState) => state.profile.user);
  const role = currentUser?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Determines if the current user can edit this course
   * - Admin: can edit any course
   * - AccountAdmin: can edit any course in their account
   * - WorkspaceAdmin: can edit courses in their workspace only
   */
  const canEdit = (): boolean => {
    if (!role || !course) return false;

    // Admin can edit any course
    if (role === UserRole.Admin) return true;

    // AccountAdmin can edit any course in their account
    if (role === UserRole.AccountAdmin) return true;

    // WorkspaceAdmin can only edit courses in their workspace
    if (role === UserRole.WorkspaceAdmin && course.workspaceId === currentUser?.workspaceId) {
      return true;
    }

    return false;
  };

  const canAssign = (): boolean => {
    if (!role || !course) return false;

    if (course?.isDraft) return false;

    if (isAdmin(role)) return true;

    return false;
  };

  const fetchCourse = async () => {
    // Skip fetch if courseId is invalid (0 or negative)
    if (!courseId || courseId <= 0) {
      setLoading(false);
      return;
    }
    try {
      const result = await coursesClient.getCourseById(courseId);
      const thumbnailUrl = await getFileUrl(result?.thumbnail);
      setCourse({ ...result, thumbnailUrl });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      setLoading(false);
    }
  };

  // Fetch thumbnail display URL when course.thumbnail changes
  useEffect(() => {
    async function loadThumbnail() {
      const url = await getFileUrl(course?.thumbnail);
      setThumbnailDisplayUrl(url);
    }
    loadThumbnail();
  }, [course?.thumbnail]);

  const openEditDialog = () => {
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
  };

  const saveCourse = async (updatedCourse: any) => {
    try {
      await coursesClient.updateCourse(course.id, updatedCourse);

      // Handle Learning Path course management
      if (updatedCourse.courseType === CourseType.LearningPath && updatedCourse.pathCourses) {
        await syncPathCourses(course.id, updatedCourse.pathCourses);
      }

      // Refetch course to get the updated path courses
      await fetchCourse();

      // Update Redux store
      dispatch(updateCourse({ id: course.id, course: { ...updatedCourse, id: course.id } }));
      alert(enqueueSnackbar, 'Course updated successfully', 'success');
      return { ...updatedCourse, id: course.id };
    } catch (error) {
      console.error('Failed to update course:', error);
      alert(enqueueSnackbar, 'Failed to update course', 'error');
      throw error;
    }
  };

  /**
   * Syncs path courses by removing old ones and adding/reordering new ones
   */
  const syncPathCourses = async (learningPathId: number, newPathCourses: PathCourseItem[]) => {
    const existingPathCourses: PathCourseItem[] = course?.pathCourses || [];

    // Find courses to remove (in existing but not in new)
    const coursesToRemove = existingPathCourses.filter(
      (existing) => !newPathCourses.some((newCourse) => newCourse.courseId === existing.courseId)
    );

    // Find courses to add (in new but not in existing)
    const coursesToAdd = newPathCourses.filter(
      (newCourse) => !existingPathCourses.some((existing) => existing.courseId === newCourse.courseId)
    );

    // Remove courses
    for (const courseToRemove of coursesToRemove) {
      try {
        await coursesClient.removeCourseFromPath(learningPathId, courseToRemove.courseId);
      } catch (error) {
        console.error(`Failed to remove course ${courseToRemove.courseId} from path:`, error);
      }
    }

    // Add new courses
    for (const courseToAdd of coursesToAdd) {
      try {
        await coursesClient.addCourseToPath(learningPathId, {
          courseId: courseToAdd.courseId,
          sequenceOrder: courseToAdd.sequenceOrder,
        });
      } catch (error) {
        console.error(`Failed to add course ${courseToAdd.courseId} to path:`, error);
      }
    }

    // Reorder courses if there are any remaining
    if (newPathCourses.length > 0) {
      try {
        const orderedCourseIds = newPathCourses
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
          .map((c) => c.courseId);
        await coursesClient.reorderPathCourses(learningPathId, { courseIds: orderedCourseIds });
      } catch (error) {
        console.error('Failed to reorder path courses:', error);
      }
    }
  };

  const deleteCourse = async (courseIdToDelete: number) => {
    try {
      await coursesClient.deleteCourse(courseIdToDelete);
      // Remove from Redux store
      dispatch(removeCourse(courseIdToDelete));
      alert(enqueueSnackbar, 'Deleted successfully', 'success');
      // Navigate back to courses list
      navigate('/course');
    } catch (error) {
      console.error('Failed to delete course:', error);
      alert(enqueueSnackbar, 'Failed to delete course', 'error');
    }
  };

  return {
    course,
    loading,
    canEdit: canEdit(),
    canAssign: canAssign(),
    thumbnailDisplayUrl,
    fetchCourse,
    // Dialog state
    showEditDialog,
    openEditDialog,
    closeEditDialog,
    saveCourse,
    deleteCourse,
  };
}
